<template>
  <div>
    <Head title="Running Balance" />
    <div class="flex items-center justify-between mb-6">
      <h1 class="mb-8 text-3xl font-bold">Running Balances</h1>
      <Link class="btn-orange" href="/uids/running-balances">
        <span>UIDs</span>
      </Link>
    </div>
    <div class="flex items-center justify-between mb-6">
      <search-input v-model="form.search" class="mr-4 w-full max-w-md"/>
      <div class="flex w-full bg-white rounded shadow mr-3">
        <multiselect
          v-model="form.location"
          placeholder="Search Location"
          :options="locations"
          :close-on-select="true"
          :searchable="true"
          :show-labels="false"
          :clear-on-select="false"
          :preserve-search="true"
          :create-option="true"
          mode="tags"
          class="w-full mr-3"
        />
      </div>
      <div class="flex w-full bg-white rounded shadow">
        <multiselect
          v-model="form.category"
          placeholder="Search Category"
          :options="categories"
          :close-on-select="true"
          :searchable="true"
          :show-labels="false"
          :clear-on-select="false"
          :preserve-search="true"
          :create-option="true"
          mode="tags"
          class="w-full mr-3"
        />
      </div>
      <button class="ml-3 text-gray-500 hover:text-gray-700 focus:text-indigo-500 text-sm" type="button" @click="reset">Reset</button>
    </div>
    <div class="flex items-center justify-between mb-6">
      <a
        v-if="stocktakes.data.length"
        class="btn-orange px-3 py-2 text-xs cursor-pointer"
        :href="`/running-balances-skus/download${url()}`"
        download
        target="__blank"
      >
        <span>Download</span>
      </a>
      <span class="text-gray-500 text-sm">Last updated: {{ data_as_at }}</span>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="pb-3 pt-3 px-3">Part Number</th>
          <th class="pb-3 pt-3 px-3">Part Description</th>
          <th class="pb-3 pt-3 px-3">Category</th>
          <th class="pb-3 pt-3 px-3">Stock Level</th>
          <th class="pb-3 pt-3 px-3">UoM</th>
        </tr>
        <tr v-for="item in stocktakes.data" :key="item.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
          <td class="border-t">
            <Link class="flex items-center px-3 py-3" :href="`/skus/${item.sku_id}/running-balances${url()}`" tabindex="-1">
              {{ item.sku }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3" :href="`/skus/${item.sku_id}/running-balances${url()}`" tabindex="-1">
              {{ item.description }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3" :href="`/skus/${item.sku_id}/running-balances${url()}`" tabindex="-1">
              {{ item.category }}
            </Link>
          </td>
          <td class="w-px border-t pb-3 pt-3">
            <Link class="flex items-end px-3 text-end justify-end" :href="`/skus/${item.sku_id}/running-balances${url()}`" tabindex="-1">
              {{ item.quantity }}
            </Link>
          </td>
          <td class="w-px border-t pb-3 pt-3">
            <Link class="flex items-end px-3 text-end justify-end" :href="`/skus/${item.sku_id}/running-balances${url()}`" tabindex="-1">
              {{ item.uom }}
            </Link>
          </td>
        </tr>
      </table>
    </div>
    <pagination class="mt-6" :links="stocktakes.links" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import Multiselect from '@vueform/multiselect'
import Checkbox from '@/Shared/Checkbox'
import SearchInput from '@/Shared/SearchInput'

export default {
  components: {
    Head,
    Link,
    Pagination,
    Multiselect,
    Checkbox,
    SearchInput,
  },
  layout: Layout,
  props: {
    filters: Object,
    stocktakes: Object,
    data_as_at: String,
    locations: Object,
    categories: Object,
  },
  data() {
    return {
      form: {
        location: this.filters.location,
        category: this.filters.category,
        search: this.filters.search,
        uid: this.filters.uid ? this.filters.uid : false,
      },
      show: false,
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/running-balances', pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    url() {
      let query = '';
      if (this.form.location) {
        query += `location=${this.form.location}`;
      }
      if (this.form.category) {
        if (query) {
          query += '&';
        }
        query += `category=${this.form.category}`;
      }
      if (this.form.uid) {
        if (query) {
          query += '&';
        }
        query += `uid=${this.form.uid}`;
      }
      if (this.form.search) {
        if (query) {
          query += '&';
        }
        query += `search=${this.form.search}`;
      }
      if (query) {
        query = `?${query}`;
      }
      return query;
    },
  },
}
</script>
<style src="@vueform/multiselect/themes/default.css"></style>
<style type="text/css">
  .multiselect-option.is-selected.is-pointed {
    background: var(--ms-option-bg-selected-pointed,#fb923c);
    color: var(--ms-option-color-selected-pointed,#fff);
  }
  .multiselect-option.is-selected {
    background: var(--ms-option-bg-selected-pointed,#fb923c);
    color: var(--ms-option-color-selected-pointed,#fff);
  }
</style>
